import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Phone, Linkedin, User } from 'lucide-react';
import BookingWidget from './BookingWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Batch 3 — public End Page renderer (Form Flow). Renders an end-page step after
// submit: headline + subheadline + an ordered list of enabled blocks. Self-contained,
// styled to match SplashScreen. Coach card lazy-fetches the public coach profile.

const extractYouTubeId = (url) => {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
};
const extractVimeoId = (url) => {
    if (!url) return null;
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return m ? m[1] : null;
};

function VideoBlock({ url }) {
    if (!url) return null;
    const yt = extractYouTubeId(url);
    const vimeo = !yt ? extractVimeoId(url) : null;
    let src = url;
    if (yt) src = `https://www.youtube.com/embed/${yt}`;
    else if (vimeo) src = `https://player.vimeo.com/video/${vimeo}`;
    return (
        <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '16 / 9' }}>
            <iframe
                src={src}
                title="Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
            />
        </div>
    );
}

function CoachCard({ block, meta, brandColor, enableAssignment, roundRobinMode }) {
    const [coach, setCoach] = useState(null);
    const [done, setDone] = useState(false);
    const ranRef = useRef(false);           // guard double-fire (StrictMode + idempotent assign)
    const props = block.props || {};

    useEffect(() => {
        if (ranRef.current) return;
        ranRef.current = true;
        const uid = meta?.coach_assigned_user_id;
        const org = meta?.org_id;
        const contactId = meta?.contact_id;
        let alive = true;

        if (uid && org) {
            // Already assigned (e.g. via Batch 4 deterministic assignment) → fetch profile.
            axios.get(`${API}/public/coach/${uid}?org_id=${encodeURIComponent(org)}`)
                .then(res => { if (alive) setCoach(res.data); })
                .catch(() => { /* 404/none → render nothing */ })
                .finally(() => { if (alive) setDone(true); });
        } else if (enableAssignment && org && contactId) {
            // Round-robin self-assign (Batch 5). Idempotent server-side.
            axios.post(`${API}/public/round-robin/assign`, {
                org_id: org, contact_id: contactId, mode: roundRobinMode || undefined,
            })
                .then(res => { if (alive) setCoach(res.data?.coach || null); })   // coach object renders directly
                .catch(() => { /* none → render nothing */ })
                .finally(() => { if (alive) setDone(true); });
        } else {
            setDone(true);
        }
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta, enableAssignment, roundRobinMode]);

    if (!done) return null;            // wait for fetch
    if (!coach) return null;           // none / 404 → render nothing

    const name = coach.display_name || coach.first_name || '';
    return (
        <div className="bg-white border border-stone-200 rounded-2xl p-5 text-left shadow-sm flex gap-4 items-start">
            {coach.photo_url ? (
                <img src={coach.photo_url} alt={name} className="w-16 h-16 rounded-full object-cover flex-shrink-0" />
            ) : (
                <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                    <User className="w-7 h-7 text-stone-400" />
                </div>
            )}
            <div className="min-w-0">
                {name && <p className="text-base font-semibold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{name}</p>}
                {coach.title && <p className="text-sm text-stone-500">{coach.title}</p>}
                {props.show_bio !== false && coach.bio && <p className="text-sm text-stone-600 mt-2 whitespace-pre-wrap">{coach.bio}</p>}
                <div className="flex flex-wrap gap-3 mt-3">
                    {props.show_phone !== false && coach.phone && (
                        <a href={`tel:${coach.phone}`} className="flex items-center gap-1.5 text-sm" style={{ color: brandColor }}>
                            <Phone className="w-4 h-4" /> {coach.phone}
                        </a>
                    )}
                    {props.show_linkedin !== false && coach.linkedin_url && (
                        <a href={coach.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm" style={{ color: brandColor }}>
                            <Linkedin className="w-4 h-4" /> LinkedIn
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

export default function EndPageRenderer({ endPage, meta, brandColor = '#1c1917', buttonColor = '#1c1917', ffNext }) {
    const ep = endPage || {};
    const blocks = [...(ep.blocks || [])]
        .filter(b => b && b.enabled !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));

    const renderBlock = (block) => {
        const p = block.props || {};
        switch (block.type) {
            case 'headline':
                return <h2 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{p.text}</h2>;
            case 'subheadline':
                return <h3 className="text-lg font-semibold text-stone-700" style={{ fontFamily: 'Manrope, sans-serif' }}>{p.text}</h3>;
            case 'body':
                return <p className="text-base text-stone-600 leading-relaxed whitespace-pre-wrap">{p.text}</p>;
            case 'video':
                return <VideoBlock url={p.url} />;
            case 'iframe':
                return p.url ? (
                    <iframe
                        src={p.url}
                        title="Embedded content"
                        className="w-full rounded-2xl border border-stone-200"
                        style={{ height: p.height || 480 }}
                        allowFullScreen
                    />
                ) : null;
            case 'cta': {
                const useFf = ffNext && (p.use_ff_next || !p.url);
                if (useFf) {
                    return (
                        <button type="button" onClick={() => { window.location.href = ffNext; }}
                            className="inline-block w-full px-6 py-3.5 rounded-xl text-white font-medium text-base min-h-[52px]"
                            style={{ backgroundColor: buttonColor }}>
                            {p.label || 'Continue'}
                        </button>
                    );
                }
                return (
                    <a href={p.url || '#'} target={p.url ? '_self' : undefined}
                        className="inline-block w-full text-center px-6 py-3.5 rounded-xl text-white font-medium text-base min-h-[52px]"
                        style={{ backgroundColor: buttonColor }}>
                        {p.label || 'Continue'}
                    </a>
                );
            }
            case 'coach_card':
                return <CoachCard block={block} meta={meta} brandColor={brandColor}
                    enableAssignment={!!ep.enable_coach_assignment} roundRobinMode={ep.round_robin_mode || null} />;
            case 'booking':
                return <BookingWidget
                    orgId={meta?.org_id}
                    contactId={meta?.contact_id}
                    coachUserId={meta?.coach_assigned_user_id}
                    roundRobinMode={ep.round_robin_mode || null}
                    brandColor={brandColor}
                    buttonColor={buttonColor} />;
            default:
                return null;
        }
    };

    return (
        <div className="text-center space-y-5">
            {ep.headline && (
                <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{ep.headline}</h1>
            )}
            {ep.subheadline && (
                <p className="text-base text-stone-600 leading-relaxed whitespace-pre-wrap">{ep.subheadline}</p>
            )}
            {blocks.map((block) => {
                const el = renderBlock(block);
                if (!el) return null;
                return <div key={block.block_id}>{el}</div>;
            })}
        </div>
    );
}
